import { Input, Modal, ModalContent, useDisclosure } from '@nextui-org/react';
import { debounce } from 'lodash';
import Image from 'next/image';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { IconCloseButton, Image1Mission, referralImage } from '@/assets';
import { ArrowNextIcon, CopyIcon } from '@/components/assets';
import { configs } from '@/configs';
import type { ListReferral } from '@/contents/attendance';
import { useRouter } from '@/provider/RouterProvider';
import { useAuth } from '@/provider/UserProvider';
import { setCodeReferral } from '@/services/provider';
import { copyClipboard, formatMMDD_YYYYHM } from '@/utils';
import Link from 'next/link';

interface ReferralProps {
  referralList: ListReferral;
}
const BASE_URL = configs.ssoCallback || 'https://airight.io';

const Referral: React.FC<ReferralProps> = ({ referralList }) => {
  const { isLoginClient, auth, setAuth } = useAuth();
  const [code, setCode] = useState('');
  const router = useRouter();
  const { isOpen, onOpenChange, onOpen } = useDisclosure();

  const URL_TWEET_REFERRAL = `https://x.com/intent/post?text=Join+aiRight+and+enjoy+many+features%2C+creating+with+AI%2C+and+earning+money.%0AMy+referral+code%3A+${auth.user.refCode}%0AWebsite%3A+https://airight.io?refBy=${auth.user.refCode}%0AFollow+%40aiRight_io+for+updates.`

  const handelSubmitRef = debounce(
    async () => {
      try {
        if (!code) return;
        const res = await setCodeReferral(code);
        if (res.status === 201) {
          setAuth({
            ...auth,
            user: {
              ...auth.user,
              credit: auth.user.credit + res.data.plusCredit,
              usedRef: {
                username: res.data.userRef?.username,
                publicAddress: res.data.userRef?.publicAddress,
                picture: res.data.userRef?.picture,
                refCode: String(res.data?.userRef?.refCode),
              },
            },
          });
        }
        toast.success(`${res.data?.plusCredit} credits has been added to your account.`);
      } catch (error) {
        console.log('err setCodeReferral', error);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  return (
    <div className="main-container flex max-w-[1048px] grow flex-col pt-10">
      <Modal
        classNames={{ header: 'p-10 md:p-5' }}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        scrollBehavior="inside"
        hideCloseButton
        placement="center"
      >
        <ModalContent className="min-w-[700px] rounded-[20px] p-5 md:min-w-[489px] sm:min-w-[90%]">
          {(onClose) => (
            <div className="relative text-16 sm:text-14">
              <div
                onClick={onClose}
                className="absolute right-0 top-0 cursor-pointer rounded-full border-1"
              >
                <IconCloseButton />
              </div>

              <p className="mb-5 text-20 font-bold underline">AIRI locked referral</p>
              <p className="mb-3">
                <strong>👥 1. Invite and Earn: </strong>
                When your invitee locks their AIRI tokens, you are eligible to receive a commission
                based on the lock duration:
                <p>
                  <span className="ml-2 font-semibold">🔒 * 3 Months Lock: </span>
                  1% commission of the total AIRI tokens locked.
                </p>
                <p>
                  <span className="ml-2 font-semibold">🔒 * 6 Months Lock: </span>
                  2% commission of the total AIRI tokens locked.
                </p>
                <p>
                  <span className="ml-2 font-semibold">🔒 * 12 Months Lock: </span>
                  5% commission of the total AIRI tokens locked.
                </p>
              </p>
              <p className="mb-3">
                <strong>💳 2. Automatic Credit: </strong>
                The commission will be automatically credited to your account once the invitee's
                staking process is complete.
              </p>
              <p className="mb-3">
                <strong>✅ 3. Eligibility: </strong>
                The commission applies only when the invitee uses your referral link and
                successfully locks their AIRI tokens for the specified duration.
              </p>
              <p className="mb-3">
                <strong>👥 4. Multiple Referrals: </strong>
                You can refer as many people as you like, and you will earn the commission based on
                the lock duration for each invitee who locks their AIRI tokens.
              </p>
            </div>
          )}
        </ModalContent>
      </Modal>

      <div
        aria-checked={!isLoginClient}
        className="flex w-full items-center justify-between rounded-2xl bg-orange-50 px-[30px] py-[20px] lg:flex-col aria-checked:items-start"
      >
        {isLoginClient ? (
          <>
            <div className="flex w-2/3 items-center justify-between text-center lg:w-full lg:max-w-[500px] sm:flex-col">
              <div className="flex flex-col sm:flex-row sm:w-full sm:items-center sm:justify-between">
                <span className="mb-2 text-14 font-semibold sm:text-12 sm:mb-0">My Credits</span>
                <span className="text-[24px] font-semibold sm:text-14">
                  {auth?.user?.credit || 0}
                </span>
              </div>
              <div className="h-[45px] w-[1px] bg-[#F0C0B0] sm:hidden" />
              <div className="flex flex-col sm:flex-row sm:w-full sm:items-center sm:justify-between">
                <span className="mb-2 text-14 font-semibold sm:text-12 sm:mb-0">
                  {' '}
                  Total Earn AIRI
                </span>
                <span className="text-[24px] font-semibold sm:text-14">
                  {referralList?.totalAiriEarnedFromReferrals || 0}
                </span>
              </div>
              <div className="h-[45px] w-[1px] bg-[#F0C0B0] sm:hidden" />
              <div className="flex flex-col sm:flex-row sm:w-full sm:items-center sm:justify-between">
                <span className="mb-2 text-14 font-semibold sm:text-12 sm:mb-0">
                  {' '}
                  Total Transferred AIRI
                </span>
                <span className="text-[24px] font-semibold sm:text-14">
                  {referralList?.totalAiriTransferred || 0}
                </span>
              </div>
            </div>
            <div className="flex flex-col justify-end lg:w-full lg:max-w-[500px] lg:mt-3 ">
              <div className="flex items-center mb-2">
                {auth?.user?.isEmailVerified ? (
                  <div className="flex items-center lg:w-full lg:max-w-[500px] justify-between">
                    <span className="text-16 font-semibold">
                      Your referral:{' '}
                      <span className="text-16 font-normal sm:hidden">{auth.user.refCode}</span>
                    </span>
                    <p
                      onClick={(e) =>
                        copyClipboard(e, `${BASE_URL}/?refBy=${auth.user.refCode}` || '')
                      }
                      className="ml-3 flex cursor-pointer items-center justify-center rounded-full bg-orange-500 p-1"
                    >
                      <CopyIcon />
                    </p>
                  </div>
                ) : (
                  <span
                    onClick={() => router.push('/update-profile')}
                    className="cursor-pointer text-14 font-normal text-blue-500 underline w-[250px] lg:w-full"
                  >
                    Verify email in profile to receive referral code
                  </span>
                )}
              </div>
              <div>
                {isLoginClient &&
                  (auth.user?.usedRef?.refCode ? (
                    <span className="text-16 font-semibold sm:text-12">
                      Use referral code:{' '}
                      <span className="text-16 font-normal">{auth.user?.usedRef?.refCode}</span>
                    </span>
                  ) : (
                    <div className="flex flex-col items-end relative">
                      <Input
                        type="number"
                        classNames={{
                          input: [
                            'dark:placeholder:text-white font-normal text-14 pl-1',
                            'rounded-[100px]',
                          ],
                          innerWrapper: '!bg-white rounded-[100px]',
                          inputWrapper:
                            '!bg-white rounded-[100px] shadow-none h-[30px] min-h-[30px]',
                          base: 'h-[30px] min-h-[30px]',
                        }}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Enter code"
                      />
                      <div
                        onClick={handelSubmitRef}
                        className="bg-orange-500 p-1 absolute right-[10px] -translate-y-1/2  top-1/2 rounded-full cursor-pointer "
                      >
                        <ArrowNextIcon />
                      </div>
                    </div>
                  ))}
              </div>
              {auth?.user?.isEmailVerified && (
                <Link className="text-[14px] sm:text-[13px] text-orange-500 font-medium mt-2 hover:opacity-80" target="_blank" href={URL_TWEET_REFERRAL}>Click to tweet out your code</Link>
              )}
            </div>
          </>
        ) : (
          <div className="flex justify-start">
            <Image
              src={Image1Mission}
              alt="avatarImag"
              width={55}
              height={55}
              className="h-[55px] w-[55px] rounded-[14px] object-cover"
            />
            <div className="pl-4">
              <p className="self-center pb-1 text-20 font-semibold leading-8">Log in to aiRight</p>
              <span className="self-center text-14 font-normal leading-5 text-gray-500">
                Log in daily to claim your reward.
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="relative my-10">
        <Image src={referralImage} alt="referralImage md:w-[140px]" />
        <div className="absolute top-1/2 flex w-full items-start justify-between md:top-[58%]">
          <div className="flex w-1/3 flex-col text-center">
            <span className="text-20 font-semibold lg:text-14 md:text-12">
              Share Your Code and Link
            </span>
            <span className="text-14 font-normal text-gray-500 lg:text-12 md:hidden">
              You can invite your friends to use all AiRight product with just one referral code.
            </span>
          </div>
          <div className="flex w-1/3 flex-col text-center">
            <span className="text-20 font-semibold lg:text-14 md:text-12">
              Connect with Your Friends
            </span>
            <span className="text-14 font-normal text-gray-500 lg:text-12 md:hidden">
              When your friends sign up using your referral code, both you and your friend will
              receive <span className="text-yellow-500 underline">20 credits</span> as a reward.{' '}
            </span>
          </div>
          <div className="flex w-1/3 flex-col text-center">
            <span className="text-20 font-semibold lg:text-14 md:text-12">Get Reward</span>
            <span className="text-14 font-normal text-gray-500 lg:text-12 md:hidden">
              Get up to 5% commission when your invitee locks their AIRI tokens. The reward will be
              automatically credited to your account once their staking is complete.
            </span>
          </div>
        </div>
      </div>

      <div className="mb-5 flex grow items-start gap-5 md:w-full md:flex-col">
        <div className="h-full w-1/2 md:w-full">
          <span className="text-16 font-semibold">Referral list</span>
          <div className="mt-3 flex h-[500px] flex-col rounded-[12px] bg-gray-50 p-5">
            <div className="mb-3 flex items-center">
              <span className="w-1/6 text-14 font-semibold">Id</span>
              <span className="w-3/6 text-14 font-semibold">Username</span>
              <span className="mr-[6px] w-2/6 text-14 font-semibold">Date</span>
            </div>
            <div className="scrollbar-custom grow overflow-hidden overflow-y-auto pr-2">
              {referralList?.referralList ? (
                referralList?.referralList?.map((item) => (
                  <div className="flex items-center border-t-1 border-gray-200 py-5 text-14 font-normal">
                    <p className="w-1/6">{item?.userProvider?.id}</p>
                    <p className="line-clamp-1 w-3/6">
                      {(item?.userProvider?.username?.length > 10
                        ? `${item?.userProvider?.username}...`
                        : item?.userProvider?.username) || ''}
                    </p>
                    <p className="w-2/6 text-gray-500">
                      {formatMMDD_YYYYHM(item?.createdAt, false)}
                    </p>
                  </div>
                ))
              ) : (
                <div className="flex h-full grow items-center justify-center">
                  <span className="text-14 font-normal text-gray-500">
                    --Empty list referral list--
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="h-full w-1/2 md:w-full">
          <div className="flex items-center justify-between">
            <span className="text-16 font-semibold">AIRI locked referral list</span>
            <p
              onClick={onOpen}
              className="cursor-pointer text-14 font-normal text-orange-500 underline"
            >
              Rules
            </p>
          </div>
          <div className="mt-3 flex h-[500px] flex-col rounded-[12px] bg-gray-50 p-5">
            <>
              <div className="mb-3 flex items-center">
                <span className="w-1/6 text-14 font-semibold">Id</span>
                <span className="w-3/6 text-14 font-semibold">Username</span>
                <span className="mr-[6px] flex w-2/6 justify-end text-14 font-semibold">
                  Revenue
                </span>
              </div>
              <div className="scrollbar-custom grow overflow-hidden overflow-y-auto pr-2">
                {referralList?.airiLockReferral ? (
                  referralList?.airiLockReferral?.map((item) => (
                    <div className="flex items-center border-t-1 border-gray-200 py-5 text-14 font-normal">
                      <p className="w-1/6">{item?.refProvider?.id}</p>
                      <p className="line-clamp-1 w-3/6">
                        {(item?.refProvider?.username?.length > 10
                          ? `${item?.refProvider?.username}...`
                          : item?.refProvider?.username) || ''}
                      </p>
                      <p className="flex w-2/6 justify-end text-14 font-semibold">
                        {item?.amount} AIRI
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="flex h-full grow items-center justify-center">
                    <span className="text-14 font-normal text-gray-500">
                      --Empty list referral list--
                    </span>
                  </div>
                )}
              </div>
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;
